/**
 * PersonalTeamUsage — the member's Usage page, "Your personal Team": what
 * the Agents working your own tickets were charged this month, Agent by
 * Agent, beside the same tokens at Claude list (matched tier).
 *
 * Reads only GET /api/yagni-code-usage/me/teams, which the server scopes to
 * the caller's own personal Teams: never anyone else's Team, never a
 * workspace total. No links to the admin-only drills; each Team links to its
 * own page. Renders nothing while loading, on a failed read, or when the
 * member has no personal Team. ADR-0028: sell side only.
 */
import { Link } from "react-router-dom";

import { Sec } from "./usageParts.js";
import {
  agentLabel,
  formatUsd,
  personalTeamBlocks,
  teamLabel,
  teamSublabel,
  type PersonalTeamBlock,
  type PersonalTeamUsageState,
} from "./usageReport.js";
import { ClaudeCells } from "./usageGlass.js";


const mono = "font-[family-name:var(--font-mono)]";
const TH = "px-4 py-3 font-semibold text-[var(--text-secondary)]";
const LINK = "text-[var(--accent)] no-underline hover:underline underline-offset-2";
const QUIET = "m-0 px-4 py-4 text-[13px] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]";

export function PersonalTeamUsage({ state }: { state: PersonalTeamUsageState }) {
  if (state.loading || !state.report) return null;
  const blocks = personalTeamBlocks(state.report);
  if (blocks.length === 0) return null;
  const plural = blocks.length > 1;
  const quietLine = plural
    ? "No Agent spend on this Team this month."
    : "No Agent spend on your personal Team this month.";

  return (
    <Sec
      k="02"
      title={plural ? "Your personal Teams" : "Your personal Team"}
      fact="Agents working your tickets"
      testId="usage-personal-teams"
    >
      {blocks.map((block) => (
        <TeamCard key={block.deskId} block={block} quietLine={quietLine} />
      ))}
    </Sec>
  );
}

function TeamCard({ block, quietLine }: { block: PersonalTeamBlock; quietLine: string }) {
  const name = teamLabel(block);
  // The owner is the reader, so only "Archived" is worth saying.
  const sublabel = teamSublabel({ archived: block.archived });
  return (
    <section className="tbl usage-tbl" aria-label={name}>
      <header className="flex items-baseline justify-between gap-3 px-4 pt-4 pb-2">
        <div className="min-w-0">
          <h3 className="m-0 text-[14px] font-semibold text-[var(--ink-strong)] font-[family-name:var(--font-ui)]">
            {name}
          </h3>
          {sublabel && (
            <div className={`text-[11px] ${mono} text-[var(--text-tertiary)]`}>{sublabel}</div>
          )}
        </div>
        {!block.archived && (
          <Link to={`/desks/${encodeURIComponent(block.deskId)}`} className={`text-[12px] ${mono} ${LINK}`}>
            Open the Team →
          </Link>
        )}
      </header>
      {block.total === null ? (
<p className={QUIET}>{quietLine}</p>
      ) : (
        <AgentTable name={name} block={block} />
      )}
    </section>
  );
}

function AgentTable({ name, block }: { name: string; block: PersonalTeamBlock }) {
  const { total } = block;
  if (total === null) return null;
  return (
    <div>
      <table aria-label={name}>
        <thead>
          <tr className="border-b border-[var(--print-border)] text-left">
            <th scope="col" className={TH}>
              Agent
            </th>
            <th scope="col" className={`${TH} num`}>
              Runs
            </th>
            <th scope="col" className={`${TH} num`}>
              Billed
            </th>
            <th scope="col" className={`${TH} num`}>
              At Claude list
            </th>
            <th scope="col" className={`${TH} num`}>
              Saved
            </th>
          </tr>
        </thead>
        <tbody>
          {block.cells.map((cell) => (
            <tr key={cell.agentId} className="border-b border-[var(--print-border)]">
              <th scope="row" className="px-4 py-3 text-left font-medium">
                {agentLabel(cell)}
              </th>
              <td className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}>
                {cell.runs.toLocaleString("en-US")}
              </td>
              <td className={`px-4 py-3 text-right ${mono}`}>{formatUsd(cell.sellMillicents)}</td>
              <ClaudeCells frontier={cell.frontier} />
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--ink)]">
            <th scope="row" className="px-4 py-3 text-left font-semibold">
              Team total
            </th>
            <td className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}>
              {total.runs.toLocaleString("en-US")}
            </td>
            <td className={`px-4 py-3 text-right ${mono} font-semibold`}>
              {formatUsd(total.sellMillicents)}
            </td>
            <ClaudeCells frontier={total.frontier} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
